import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { Course } from '@/client/courses';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  getFirstVideoFallbackLanguage,
  hasVideoFallback,
} from '@/utils/helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CourseLanguageChanger } from './CourseLanguageChanger';
import { VideoFallbackMessage } from './VideoFallbackMessage';

const StyledGrayFlexContainer = styled(FlexContainer)`
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  background-color: var(--beige-main);
  padding: var(--default-padding);
`;

type CourseLanguageSelectScreennProps = {
  course: Course;
  initialLanguage?: LanguagesEnum;
  isLoading?: boolean;
  isDisabled?: boolean;
  isMaterial?: boolean;
  setIsCourseStarted: (isStarted: boolean) => void;
  setSelectedLanguage: (language: LanguagesEnum) => void;
};

export const CourseLanguageSelectScreen: React.FC<
  CourseLanguageSelectScreennProps
> = ({
  course,
  initialLanguage,
  isLoading,
  isDisabled,
  isMaterial,
  setIsCourseStarted,
  setSelectedLanguage,
}) => {
  const { t } = useTranslation();
  const [language, setLanguage] = useState<LanguagesEnum | undefined>(
    initialLanguage,
  );

  useEffect(() => {
    if (initialLanguage) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage]);

  return (
    <StyledGrayFlexContainer
      direction="column"
      flex="1"
      translate="no"
      className="notranslate"
    >
      <h2>
        {isMaterial
          ? t('material.choose.language')
          : t('course.choose.language')}
      </h2>
      <FlexContainer className="mt-4" wrap="wrap" gap={12}>
        <CourseLanguageChanger
          language={language}
          course={course}
          onChange={(value) => setLanguage(value)}
        />
        <AppButton
          label={t('generic.continue')}
          className="ml-2"
          onClick={() => {
            if (language) {
              setSelectedLanguage(language);
              setIsCourseStarted(true);
            }
          }}
          isDisabled={isDisabled || isLoading}
          icon={isLoading ? 'pi pi-spinner' : ''}
        />
      </FlexContainer>
      {language && hasVideoFallback(course.courseContents, language) && (
        <VideoFallbackMessage
          severity="info"
          text={t('course.video.fallback.languageSelect.v2', {
            fallbackLanguage:
              languageDisplayLabel[
                getFirstVideoFallbackLanguage(
                  course.courseContents,
                  language,
                ) ?? LanguagesEnum.EN
              ],
          })}
          className="w-full mt-3"
          style={{ maxWidth: '550px' }}
        />
      )}
    </StyledGrayFlexContainer>
  );
};
